import React from 'react';
import { Radio as AntRadio, RadioProps as AntRadioProps, RadioGroupProps as AntRadioGroupProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const { Group, Button } = AntRadio;
type AntRadioButtonProps = React.ComponentProps<typeof Button>;

export interface RadioProps extends Omit<AntRadioProps, 'className'> {
  className?: string;
  status?: 'error' | 'warning';
  size?: 'small' | 'middle' | 'large';
}

type RadioStyleType = 'radio' | 'group' | 'button';

interface RadioStylesParams {
  status?: 'error' | 'warning';
  size?: 'small' | 'middle' | 'large';
  direction?: 'horizontal' | 'vertical';
  className?: string;
  type: RadioStyleType;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const getBaseRadioStyles = () => ({
    // Unchecked hover state - border changes to blue
    '& .ant-radio:hover .ant-radio-inner': {
      borderColor: theme.color.semantic.brand.default.moderate,
    },
    // Radio inner circle - unchecked default state
    '& .ant-radio-inner': {
      width: theme.spacing[4],
      height: theme.spacing[4],
      borderColor: theme.color.semantic.border.primary,
      borderWidth: theme.border.width.sm,
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderRadius: theme.radius.full,
      transition: 'all 0.2s ease',
      '&:after': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderRadius: theme.radius.full,
        transition: 'transform 0.2s ease',
      },
    },
    // Checked state
    '& .ant-radio-checked .ant-radio-inner': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      borderColor: theme.color.semantic.brand.default.primary,
      '&:after': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
      },
    },
    // Checked hover state
    '& .ant-radio-checked:hover .ant-radio-inner': {
      backgroundColor: theme.color.semantic.brand.default.secondary,
      borderColor: theme.color.semantic.brand.default.secondary,
    },
    // Focus state
    '& .ant-radio-input:focus + .ant-radio-inner': {
      borderColor: theme.color.semantic.brand.default.primary,
    },
    // Disabled unchecked state
    '& .ant-radio-disabled': {
      cursor: 'not-allowed',
      '& .ant-radio-inner': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.border.subtle,
        cursor: 'not-allowed',
      },
      '& .ant-radio-input': {
        cursor: 'not-allowed',
      },
      '& + span': {
        color: theme.color.semantic.text.soft,
        cursor: 'not-allowed',
      },
    },
    // Disabled checked state
    '& .ant-radio-disabled.ant-radio-checked .ant-radio-inner': {
      backgroundColor: theme.color.semantic.background.neutral.strong,
      borderColor: theme.color.semantic.border.strong,
      '&:after': {
        backgroundColor: theme.color.semantic.background.neutral.secondary,
      },
    },
    // Label text
    '& .ant-radio + span': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
      paddingTop: theme.spacing[2],
      paddingBottom: theme.spacing[2],
      paddingLeft: theme.spacing[4],
      paddingRight: theme.spacing[4],
      display: 'inline-block',
      verticalAlign: 'middle',
    },
  });

  return {
    radio: {
      ...getBaseRadioStyles(),
      // Wrapper alignment
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: theme.typography.lineHeight.tight,
      verticalAlign: 'middle',
      '& .ant-radio': {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: theme.spacing[0],
        marginTop: theme.spacing[0],
        marginBottom: theme.spacing[0],
        verticalAlign: 'middle',
      },
      '& .ant-radio-inner': {
        display: 'inline-block',
        verticalAlign: 'middle',
        borderRadius: theme.radius.full,
      },
      // Small size
      '&.ant-radio-wrapper-sm': {
        '& .ant-radio-inner': {
          width: theme.spacing[3],
          height: theme.spacing[3],
          '&:after': {
            width: '6px',
            height: '6px',
          },
        },
        '& .ant-radio + span': {
          fontSize: theme.typography.fontSize.sm,
        },
      },
      // Large size
      '&.ant-radio-wrapper-lg': {
        '& .ant-radio-inner': {
          width: theme.spacing[5],
          height: theme.spacing[5],
          '&:after': {
            width: theme.spacing[3],
            height: theme.spacing[3],
          },
        },
        '& .ant-radio + span': {
          fontSize: theme.typography.fontSize.lg,
        },
      },
      // Error status
      '&.radio-status-error': {
        '& .ant-radio-inner': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
        '&:hover .ant-radio-inner': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
        '& .ant-radio-input:focus + .ant-radio-inner': {
          borderColor: theme.color.semantic.brand.danger.light,
          boxShadow: `0 0 0 2px ${theme.color.semantic.brand.danger.subtle}`,
        },
        '& .ant-radio-checked .ant-radio-inner': {
          backgroundColor: theme.color.semantic.brand.danger.light,
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      // Warning status
      '&.radio-status-warning': {
        '& .ant-radio-inner': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '&:hover .ant-radio-inner': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '& .ant-radio-input:focus + .ant-radio-inner': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '& .ant-radio-checked .ant-radio-inner': {
          backgroundColor: theme.color.semantic.brand.default.primary,
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
    },
    // Radio Group styles
    radioGroup: {
      ...getBaseRadioStyles(),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing[2],
      '&.ant-radio-group-horizontal': {
        flexDirection: 'row',
        flexWrap: 'wrap',
      },
      '& .ant-radio-wrapper': {
        marginRight: theme.spacing[0],
        marginBottom: theme.spacing[0],
      },
    },
    // Radio Button styles
    radioButton: {
      '&.ant-radio-button-wrapper': {
        height: 'auto',
        minHeight: theme.spacing[8],
        padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.regular,
        lineHeight: theme.typography.lineHeight.normal,
        color: theme.color.semantic.text.primary,
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.border.primary,
        borderWidth: theme.border.width.sm,
        borderRadius: theme.radius.sm,
        transition: 'all 0.2s ease',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          color: theme.color.semantic.brand.default.primary,
          borderColor: theme.color.semantic.brand.default.primary,
        },
        '&:first-child': {
          borderRadius: `${theme.radius.sm} 0 0 ${theme.radius.sm}`,
        },
        '&:last-child': {
          borderRadius: `0 ${theme.radius.sm} ${theme.radius.sm} 0`,
        },
        '&:not(:first-child)::before': {
          backgroundColor: theme.color.semantic.border.primary,
        },
      },
      '&.ant-radio-button-wrapper-checked': {
        color: theme.color.semantic.text.inverse,
        backgroundColor: theme.color.semantic.brand.default.primary,
        borderColor: theme.color.semantic.brand.default.primary,
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.soft,
          borderColor: theme.color.semantic.brand.default.soft,
        },
        '&::before': {
          backgroundColor: theme.color.semantic.brand.default.primary,
        },
      },
      '&.ant-radio-button-wrapper-disabled': {
        color: theme.color.semantic.text.soft,
        backgroundColor: theme.color.semantic.background.neutral.secondary,
        borderColor: theme.color.semantic.border.subtle,
        cursor: 'not-allowed',
        '&.ant-radio-button-wrapper-checked': {
          backgroundColor: theme.color.semantic.background.neutral.strong,
          borderColor: theme.color.semantic.border.subtle,
          color: theme.color.semantic.text.primary,
        },
      },
      // Small size
      '&.ant-radio-button-wrapper-sm': {
        minHeight: theme.spacing[6],
        padding: `${theme.spacing[1]} ${theme.spacing[3]}`,
        fontSize: theme.typography.fontSize.sm,
      },
      // Large size
      '&.ant-radio-button-wrapper-lg': {
        minHeight: '40px',
        padding: `${theme.spacing[3]} ${theme.spacing[5]}`,
        fontSize: theme.typography.fontSize.lg,
      },
      // Error status
      '&.radio-button-status-error': {
        borderColor: theme.color.semantic.brand.danger.light,
        '&:hover': {
          borderColor: theme.color.semantic.brand.danger.light,
        },
        '&.ant-radio-button-wrapper-checked': {
          backgroundColor: theme.color.semantic.brand.danger.light,
          borderColor: theme.color.semantic.brand.danger.light,
        },
      },
      // Warning status
      '&.radio-button-status-warning': {
        borderColor: theme.color.semantic.accent.orange.strong,
        '&:hover': {
          borderColor: theme.color.semantic.accent.orange.strong,
        },
        '&.ant-radio-button-wrapper-checked': {
          backgroundColor: theme.color.semantic.brand.default.primary,
          borderColor: theme.color.semantic.accent.orange.strong,
        },
      },
    },
  };
});

const useRadioStyles = ({
  status,
  size,
  direction,
  className = '',
  type,
}: RadioStylesParams): string => {
  const classes = useStyles();

  const getBaseClass = () => {
    switch (type) {
      case 'radio':
        return classes.radio;
      case 'group':
        return classes.radioGroup;
      case 'button':
        return classes.radioButton;
    }
  };

  const getStatusClass = () => {
    if (!status) return '';
    return type === 'button' ? `radio-button-status-${status}` : `radio-status-${status}`;
  };

  const getSizeClass = () => {
    if (!size || type === 'group') return '';
    if (type === 'radio') {
      return size === 'small' ? 'ant-radio-wrapper-sm' : size === 'large' ? 'ant-radio-wrapper-lg' : '';
    }
    return size === 'small' ? 'ant-radio-button-wrapper-sm' : size === 'large' ? 'ant-radio-button-wrapper-lg' : '';
  };

  const getDirectionClass = () => {
    if (type !== 'group' || !direction) return '';
    return direction === 'horizontal' ? 'ant-radio-group-horizontal' : '';
  };

  return `${getBaseClass()} ${getStatusClass()} ${getSizeClass()} ${getDirectionClass()} ${className}`.trim();
};

const RadioComponent: React.FC<RadioProps> = ({
  className = '',
  status,
  size,
  ...rest
}) => {
  const combinedClassName = useRadioStyles({ status, size, className, type: 'radio' });

  return (
    <AntRadio
      {...rest}
      className={combinedClassName}
    />
  );
};

export interface RadioGroupProps extends Omit<AntRadioGroupProps, 'className'> {
  className?: string;
  status?: 'error' | 'warning';
  direction?: 'horizontal' | 'vertical';
}

const RadioGroupComponent: React.FC<RadioGroupProps> = ({
  className = '',
  status,
  direction = 'vertical',
  ...rest
}) => {
  const combinedClassName = useRadioStyles({ status, direction, className, type: 'group' });

  return (
    <Group
      className={combinedClassName}
      {...rest}
    />
  );
};

export interface RadioButtonProps extends Omit<AntRadioButtonProps, 'className'> {
  className?: string;
  status?: 'error' | 'warning';
  size?: 'small' | 'middle' | 'large';
}

const RadioButtonComponent: React.FC<RadioButtonProps> = ({
  className = '',
  status,
  size,
  ...rest
}) => {
  const combinedClassName = useRadioStyles({ status, size, className, type: 'button' });

  return (
    <Button
      className={combinedClassName}
      {...rest}
    />
  );
};

export const Radio = RadioComponent as typeof RadioComponent & {
  Group: typeof RadioGroupComponent;
  Button: typeof RadioButtonComponent;
};

Radio.Group = RadioGroupComponent;
Radio.Button = RadioButtonComponent;

export default Radio;
